/**
 * The player's settings: what they chose, kept between visits.
 *
 * Stored on its own key, apart from the save, because wiping the week is not
 * wiping how loud you like it. Every read is guarded: a private window, a
 * blocked storage or a hand-edited value gives the defaults rather than a game
 * that will not start.
 */

const KEY = 'lionandcrown.settings';

export const DEFAULT_SETTINGS = Object.freeze({
  /** 0..1, over everything. */
  master: 0.7,
  /** 0..1, the ambient score. Quiet by default: it sits under the world. */
  music: 0.35,
  /** 0..1, every sound the world makes. */
  effects: 1,
  /** Look speed, mouse and right stick alike. 1 is the tuned default. */
  sensitivity: 1,
  invertY: false,
  /** The GPS line on the floor and the map to wherever the objective is. V or d-pad up. */
  routeLine: true,
});

/** The ranges, so a slider and a hand-edited value are held to the same thing. */
export const SETTING_RANGES = Object.freeze({
  master: [0, 1],
  music: [0, 1],
  effects: [0, 1],
  sensitivity: [0.25, 3],
});

/** Clamps and fills in, so whatever came out of storage is something we can use. */
export function cleanSettings(raw) {
  const out = { ...DEFAULT_SETTINGS };
  if (!raw || typeof raw !== 'object') return out;
  for (const [name, [lo, hi]] of Object.entries(SETTING_RANGES)) {
    const v = Number(raw[name]);
    if (Number.isFinite(v)) out[name] = Math.min(hi, Math.max(lo, v));
  }
  if (typeof raw.invertY === 'boolean') out.invertY = raw.invertY;
  if (typeof raw.routeLine === 'boolean') out.routeLine = raw.routeLine;
  return out;
}

export function createSettings({ storage = globalThis.localStorage } = {}) {
  let current = DEFAULT_SETTINGS;
  try {
    current = cleanSettings(JSON.parse(storage?.getItem(KEY) ?? 'null'));
  } catch {
    current = { ...DEFAULT_SETTINGS };
  }
  const listeners = new Set();

  function save() {
    try {
      storage?.setItem(KEY, JSON.stringify(current));
    } catch {
      // Storage refused. The setting still holds for this visit.
    }
  }

  return {
    get values() {
      return current;
    },
    get(name) {
      return current[name];
    },
    set(name, next) {
      current = cleanSettings({ ...current, [name]: next });
      save();
      for (const listener of listeners) listener(current, name);
      return current[name];
    },
    onChange(listener) {
      listeners.add(listener);
      listener(current, null);
      return () => listeners.delete(listener);
    },
  };
}
